import { DeleteOutlined, EditOutlined, MoreOutlined } from '@ant-design/icons';
import { Waypoint } from '@omniafishing/core';
import { Dropdown, MenuProps } from 'antd';
import React, { useCallback, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useDispatch } from 'react-redux';
import { useResponsive } from '../../../../hooks/use_responsive';
import { apiV1 } from '../../../../lib/api';
import { objectToFormData } from '../../../../lib/form';
import { WebAnalytics } from '../../../../lib/web_analytics';
import { FlashMessageActions } from '../../../../redux/flash_message';
import { OmniaButton, OmniaButtonProps } from '../../../omnia_button/omnia_button';
import { SvgShare } from '../../../svg/share';
import { getWaypointShareUrl } from './share_waypoint_button';
import { WaypointDeleteModal } from './waypoint_delete_modal';
import styles from './waypoint_more_button.less';

interface WaypointMoreButtonProps {
  waypoint: Waypoint;
  onEditableWaypointChange: (waypoint: Waypoint | null, flyTo?: boolean) => void;
  onWaypointDelete: (waypointId: number) => void;
  buttonProps?: Partial<OmniaButtonProps>;
}
export const WaypointMoreButton = (props: WaypointMoreButtonProps) => {
  const { waypoint, onEditableWaypointChange, onWaypointDelete, buttonProps } = props;
  const [isOpen, setIsOpen] = useState(false);
  const { isDesktop } = useResponsive();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const closeDropdown = useCallback(() => setIsDropdownOpen(false), []);

  const updateWaypointToPublic = useCallback(async () => {
    try {
      const formData = objectToFormData({ public: true });
      await apiV1.waypointUpdate(waypoint.id, formData);
    } catch (error) {
      dispatch(
        FlashMessageActions.FLASH_MESSAGE_SET({
          header: 'Error',
          subheader:
            'We had a problem making your waypoint public so you can share. Please try again.',
        })
      );
    }
  }, [waypoint.id]);

  const dispatch = useDispatch();

  const fullWaypointUrl = getWaypointShareUrl(waypoint);

  const shareData = {
    url: fullWaypointUrl,
  };

  const handleNavigatorClick = () => {
    updateWaypointToPublic();
    dispatch(
      FlashMessageActions.FLASH_MESSAGE_SET({
        header: 'Link Copied',
        subheader: 'A link to this waypoint has been copied to your clipboard.',
      })
    );
    navigator
      .share(shareData)
      .then(() => {
        WebAnalytics.waypointShared({
          id_hash: waypoint.id_hash,
        });
      })
      .catch((e) => {
        console.error(e);
      });
  };

  const items: MenuProps['items'] = [
    {
      label: (
        <span
          className={styles.label}
          onClick={(e) => {
            e.stopPropagation();
            closeDropdown();
            onEditableWaypointChange(waypoint, true);
          }}
        >
          <EditOutlined />
          <span className={styles.labelText}>Edit</span>
        </span>
      ),
      key: '0',
    },
    {
      label: (
        <span
          className={styles.label__share}
          onClick={(e) => {
            e.stopPropagation();
            closeDropdown();
          }}
        >
          {typeof navigator !== 'undefined' && navigator.canShare?.(shareData) ? (
            <CopyToClipboard text={fullWaypointUrl} onCopy={handleNavigatorClick}>
              <div className={styles.shareButton}>
                <span className={styles.shareIcon}>
                  <SvgShare />
                </span>
                <span className={styles.labelText}>Share</span>
              </div>
            </CopyToClipboard>
          ) : (
            <div>
              <CopyToClipboard
                text={fullWaypointUrl}
                onCopy={() => {
                  dispatch(
                    FlashMessageActions.FLASH_MESSAGE_SET({
                      header: 'Link Copied',
                      subheader: 'A link to this waypoint has been copied to your clipboard.',
                    })
                  );
                  updateWaypointToPublic();
                  WebAnalytics.waypointShared({
                    id_hash: waypoint.id_hash,
                  });
                }}
              >
                <div className={styles.shareButton}>
                  <span className={styles.shareIcon}>
                    <SvgShare />
                  </span>
                  <span className={styles.labelText}>Share</span>
                </div>
              </CopyToClipboard>
            </div>
          )}
        </span>
      ),
      key: '1',
    },
    {
      label: (
        <span
          className={styles.label}
          onClick={(e) => {
            e.stopPropagation();
            closeDropdown();
            setIsOpen(true);
          }}
        >
          <DeleteOutlined />
          <span className={styles.labelText}>Delete</span>
        </span>
      ),
      key: '2',
    },
  ];

  return (
    <div className={styles.wrapper}>
      <Dropdown
        menu={{ items }}
        trigger={['click']}
        open={isDropdownOpen}
        onOpenChange={(open) => setIsDropdownOpen(open)}
        getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
      >
        <OmniaButton
          shape={isDesktop ? 'square' : 'circle'}
          kind={isDesktop ? 'text' : 'tertiary'}
          size={isDesktop ? 'sm' : 'md'}
          onClick={(e) => {
            e.preventDefault();
          }}
          {...buttonProps}
        >
          <MoreOutlined />
        </OmniaButton>
      </Dropdown>
      <WaypointDeleteModal
        isOpen={isOpen}
        waypoint={waypoint}
        onOpenChange={setIsOpen}
        onWaypointDelete={onWaypointDelete}
      />
    </div>
  );
};
